import React, { useState, useEffect } from 'react';
import { LogOut, BadgeCheck, Settings, MessageCircleCode, CreditCard, Puzzle, FolderOpen, Aperture, LayoutDashboard, Key, BookOpen } from 'lucide-react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../stores/useFeatureFlags';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui-kit/dropdown-menu';
import { Button } from '../ui-kit/button';
import { useTutorialStore, getStepsForTutorial } from './tutorials';
import { useUserDataStore } from '@/stores/useUserDataStore';
import { isStaffRole } from '@/shared/pricingPlans';

export const TopNavBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const userRole = useUserDataStore((state) => state.userData.role);
  const {
    isRunning: isTutorialRunning,
    currentTutorialId,
    stepIndex: tutorialStepIndex,
    guidedFilterPostApplyActive,
    guidedFilterPostApplyStep,
  } = useTutorialStore();
  const tutorialSteps = currentTutorialId ? getStepsForTutorial(currentTutorialId) : [];
  const currentStepId = tutorialSteps[tutorialStepIndex]?.id;

  // State to track panel visibility
  const [isRightPanelExpanded, setIsRightPanelExpanded] = useState(() => {
    const saved = localStorage.getItem('workbenchRightPanelExpanded');
    return saved === null ? true : saved === 'true';
  });
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // When tutorial is on account-menu or api-key-menu-link step, open the account dropdown
  useEffect(() => {
    if (isTutorialRunning && (currentStepId === 'account-menu' || currentStepId === 'api-key-menu-link')) {
      setAccountMenuOpen(true);
    }
  }, [isTutorialRunning, currentStepId]);

  // Guided filter post-apply step 2: expand Settings menu for combined highlight
  useEffect(() => {
    if (!guidedFilterPostApplyActive) return;
    setAccountMenuOpen(guidedFilterPostApplyStep === 2);
  }, [guidedFilterPostApplyActive, guidedFilterPostApplyStep]);

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('workbenchRightPanelExpanded');
      setIsRightPanelExpanded(saved === null ? true : saved === 'true');
    };

    window.addEventListener('localStorageChange', handleStorageChange);
    return () => window.removeEventListener('localStorageChange', handleStorageChange);
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Threat Studio' },
    { path: '/assets', icon: FolderOpen, label: 'Assets' },
    { path: '/api', icon: MessageCircleCode, label: 'API' },
  ];

  return (
    <div className="w-full border-border bg-background px-0">
      <div className="px-6">
        <div className="flex items-center justify-between py-3">
          {/* Left side - Logo and main navigation */}
          <div className="flex items-center gap-6">
            <Link to="/dashboard" className="flex items-center gap-2 text-2xl font-semibold">
              <img src="/Truss_favicon.ico" alt="Truss Logo" className="w-10 h-8" />
              <span>Truss</span>
            </Link>
            
            {/* Main navigation items */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const isApiTab = item.path === '/api';
                return (
                  <Button
                    key={item.path}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigation(item.path)}
                    className={`flex items-center gap-2 text-base ${
                      isActive 
                        ? 'font-bold border-b-2 border-primary text-primary' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    {...(isApiTab ? { 'data-tutorial': 'api-tab' } : {})}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Button>
                );
              })}
            </nav>
          </div>

          {/* Right side - AI Search, Settings dropdown and filters */}
          <div className="flex items-center gap-4">

            {/* Right Panel Toggle - Available on all pages */}
            <Button
              variant="ghost"
              size="sm"
              className="p-2"
              title="Toggle Tools Panel"
              data-tutorial="ai-panel-toggle"
              onClick={() => {
                const newState = !isRightPanelExpanded;
                setIsRightPanelExpanded(newState);
                localStorage.setItem('workbenchRightPanelExpanded', newState.toString());
                
                // Dispatch custom event to notify all pages
                window.dispatchEvent(new Event('localStorageChange'));
              }}
            >
              {isRightPanelExpanded ? (
                <Aperture className="h-5 w-5" />
              ) : (
                <Aperture className="h-5 w-5 opacity-50" />
              )}
            </Button>

            {/* Settings dropdown */}
            <DropdownMenu open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2" data-tutorial="account-menu">
                  <Settings className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48" data-tutorial="account-menu-content">
                <DropdownMenuItem onClick={() => handleNavigation('/my-account')} data-tutorial="account-menu-link">
                  <BadgeCheck className="h-4 w-4 mr-2" />
                  Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/api-key')} data-tutorial="api-key-menu-link">
                  <Key className="h-4 w-4 mr-2" />
                  API Key
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/billing')}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleNavigation('/resources')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Resources
                </DropdownMenuItem>
                {isFeatureEnabled('integrators') && (
                  <DropdownMenuItem onClick={() => handleNavigation('/integrators')}>
                    <Puzzle className="h-4 w-4 mr-2" />
                    Integrators
                  </DropdownMenuItem>
                )}
                {isStaffRole(userRole) && (
                  <DropdownMenuItem onClick={() => handleNavigation('/admin/setup-guides')}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Setup guides
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleNavigation('/signout')}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}; 